import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Cue, NodeCue, NodeList, parseSync } from "subtitle";

import { ErrorSeverity, useErrorHandler } from "../../hooks/useErrorHandler";

export const buildCaptionsFromPlaylist = async (
  playlistText: string,
  captionsURL: string,
  fetcher: typeof fetch,
): Promise<string | null> => {
  const lines = playlistText.split("\n");
  const segmentUrls = lines
    .map(line => line.trim())
    .filter(line => line.length > 0 && !line.startsWith("#"))
    .map(line => new URL(line, captionsURL).toString());

  if (segmentUrls.length === 0) {
    return null;
  }

  const segments = await Promise.all(
    segmentUrls.map(async url => {
      const segmentResponse = await fetcher(url, { cache: "no-store" });
      return segmentResponse.text();
    }),
  );

  const stripHeader = (content: string) => {
    if (content.startsWith("WEBVTT")) {
      const headerEnd = content.indexOf("\n");
      return headerEnd >= 0 ? content.slice(headerEnd + 1) : "";
    }
    return content;
  };

  return segments
    .map((segment, index) => (index === 0 ? segment : stripHeader(segment)))
    .filter(segment => segment.trim().length > 0)
    .join("\n\n");
};

export function useCaptions(
  captionsEnabled = false,
  captionsURL?: string,
  position = 0,
  hasNoAudio = false,
  slideId?: string | null,
) {
  const [parsed, setParsed] = useState<NodeList>();
  const [text, setText] = useState("\n\n");
  const [loading, setLoading] = useState(false);
  const lastLoadedUrlRef = useRef<string | null>(null);
  const activeSlideIdRef = useRef<string | null>(null);
  const loadSequenceRef = useRef(0);
  const { reportError } = useErrorHandler();
  const { t } = useTranslation();

  useEffect(() => {
    activeSlideIdRef.current = slideId ?? null;
  }, [slideId]);

  function getText(cue: Cue): string {
    return cue.text;
  }

  useEffect(() => {
    if (hasNoAudio) {
      const noAudioText = `[${t("noAudioSlide")}]`;
      if (text !== noAudioText) setText(noAudioText);
      return;
    }

    const positionMs = position * 1000;
    const isActive = (cue: NodeCue): boolean => {
      return positionMs >= cue.data.start && positionMs < cue.data.end;
    };

    const cue: NodeCue | undefined = parsed?.find(node => node.type === "cue" && isActive(node)) as NodeCue;
    if (cue) {
      const activeText = getText(cue.data);
      if (activeText !== text) setText(activeText);
    } else if (text !== "\n\n") {
      setText("\n\n");
    }
  }, [position, parsed, hasNoAudio, t, text]);

  const loadCaptions = useCallback(
    async (captionsURL: string) => {
      const currentLoadId = loadSequenceRef.current + 1;
      loadSequenceRef.current = currentLoadId;
      const loadSlideId = activeSlideIdRef.current;
      try {
        setLoading(true);
        setParsed(undefined);
        const response = await fetch(captionsURL, { cache: "no-store" });
        const subtitles = await response.text();
        const isPlaylist = captionsURL.endsWith(".m3u8") || subtitles.startsWith("#EXTM3U");
        let captionText = subtitles;

        // Handle HLS playlists, which is how Cloudflare Video serves captions for HLS streams
        // In that case, the captionsURL is the playlist URL, not a single .vtt file URL
        // For this we need to parse the playlist, fetch the VTT segments, and stitch them
        // into a single string of VTT content
        if (isPlaylist) {
          const playlistCaptions = await buildCaptionsFromPlaylist(subtitles, captionsURL, fetch);
          if (playlistCaptions !== null) {
            captionText = playlistCaptions;
          }
        }

        if (captionText.trim().length === 0) {
          reportError(ErrorSeverity.LOW, "Caption string is empty", { captionsURL });
        }

        if (loadSequenceRef.current === currentLoadId && activeSlideIdRef.current === loadSlideId) {
          setParsed(parseSync(captionText));
        }
      } catch (error) {
        console.error("Failed to load captions", error);
        reportError(ErrorSeverity.LOW, "Failed to load captions", { captionsURL });
      } finally {
        if (loadSequenceRef.current === currentLoadId && activeSlideIdRef.current === loadSlideId) {
          setLoading(false);
        }
      }
    },
    [reportError],
  );

  useEffect(() => {
    if (hasNoAudio) {
      lastLoadedUrlRef.current = null;
      setParsed(undefined);
      return;
    }

    if (captionsEnabled && !hasNoAudio && captionsURL) {
      if (lastLoadedUrlRef.current === captionsURL) {
        return;
      }
      lastLoadedUrlRef.current = captionsURL;
      loadCaptions(captionsURL);
      return;
    }
    if (!captionsURL || !captionsEnabled) {
      lastLoadedUrlRef.current = null;
      setParsed(undefined);
      setText(prevText => (prevText === "\n\n" ? prevText : "\n\n"));
    }
  }, [captionsEnabled, loadCaptions, hasNoAudio, captionsURL, slideId]);

  useEffect(() => {
    lastLoadedUrlRef.current = null;
    setParsed(undefined);
    loadSequenceRef.current += 1;
    setLoading(false);
    if (hasNoAudio) {
      return;
    }
    // \n\n is the app’s “blank captions” sentinel. The renderer expects two line breaks
    // to clear the captions region without collapsing its layout
    setText(prevText => (prevText === "\n\n" ? prevText : "\n\n"));
  }, [hasNoAudio, slideId]);

  return { text, loadCaptions, loading };
}
